import { useEffect, useRef, useState, useCallback } from "react";
import { MessageSquare, Plus, Send, X, Loader2, Users, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Avatar } from "@/components/common/ui";
import { cn } from "@/lib/utils";

const ROLE_LABELS = {
  admin: "Administrator", executive: "Executive", program_manager: "Program Manager",
  nurse: "Nurse", dsp: "DSP", clinician: "Clinician", compliance: "Compliance", technician: "Technician",
};

export default function Messages() {
  const { user } = useAuth();
  const [convos, setConvos] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadConvos = useCallback(async () => {
    try { const { data } = await api.get("/staff-messages/conversations"); setConvos(data.conversations); return data.conversations; }
    catch (e) { setConvos([]); return []; }
  }, []);

  useEffect(() => { loadConvos(); }, [loadConvos]);

  const openConvo = async (id) => {
    setActiveId(id); setThread(null);
    try { const { data } = await api.get(`/staff-messages/conversations/${id}`); setThread(data); loadConvos(); }
    catch (e) { toast.error("Could not open conversation"); }
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async () => {
    if (!text.trim() || !activeId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/staff-messages/conversations/${activeId}/messages`, { body: text });
      setThread((t) => ({ ...t, messages: [...t.messages, data] }));
      setText(""); loadConvos();
    } catch (e) { toast.error("Send failed"); }
    finally { setSending(false); }
  };

  if (!convos) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  return (
    <div>
      <PageHeader overline="Workspace · Secure Messaging" title="Messages"
        description="Unified, auditable secure messaging across staff, clinicians and managers."
        actions={<button data-testid="compose-btn" onClick={() => setComposeOpen(true)}
          className="flex items-center gap-2 rounded-md bg-[#155EEF] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#0F4FD1]"><Plus size={16} /> New Message</button>} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12" style={{ height: "calc(100vh - 220px)" }}>
        {/* Conversation list */}
        <div className="lg:col-span-4 overflow-hidden rounded-xl border bg-card shadow-sm">
          <div className="careiq-scroll h-full divide-y overflow-y-auto">
            {convos.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No conversations yet. Start a new message.</div>}
            {convos.map((c) => (
              <button key={c.id} data-testid={`convo-${c.id}`} onClick={() => openConvo(c.id)}
                className={cn("flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50", activeId === c.id && "bg-[#155EEF]/5")}>
                <Avatar name={c.last_sender} size={38} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold">{c.subject}</span>
                    {c.unread > 0 && <span data-testid={`convo-unread-${c.id}`} className="flex h-4 min-w-4 items-center justify-center rounded-full bg-[#155EEF] px-1 text-[10px] font-bold text-white">{c.unread}</span>}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{c.participant_emails.length} participants</div>
                  <div className="truncate text-xs text-muted-foreground">{c.last_sender}: {c.last_preview}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className="lg:col-span-8 flex flex-col overflow-hidden rounded-xl border bg-card shadow-sm">
          {!activeId && <div className="flex flex-1 flex-col items-center justify-center text-muted-foreground"><MessageSquare size={36} className="mb-2 opacity-40" /><span className="text-sm">Select a conversation</span></div>}
          {activeId && !thread && <div className="flex flex-1 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>}
          {thread && (
            <>
              <div className="border-b px-5 py-3">
                <div className="text-sm font-bold">{thread.conversation.subject}</div>
                <div className="mt-0.5 flex flex-wrap gap-1.5">
                  {thread.conversation.participant_emails.map((e) => (
                    <span key={e} className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{thread.conversation.participant_names?.[e] || e}</span>
                  ))}
                </div>
              </div>
              <div className="careiq-scroll flex-1 space-y-3 overflow-y-auto p-5">
                {thread.messages.map((m) => {
                  const mine = m.sender_email === user?.email;
                  return (
                    <div key={m.id} className={cn("flex gap-2.5", mine && "flex-row-reverse")}>
                      <Avatar name={m.sender_name} size={30} />
                      <div className={cn("max-w-[75%] rounded-2xl px-3.5 py-2 text-sm", mine ? "bg-[#155EEF] text-white" : "bg-muted")}>
                        {!mine && <div className="mb-0.5 text-[11px] font-semibold text-muted-foreground">{m.sender_name}</div>}
                        <div className="whitespace-pre-wrap leading-snug">{m.body}</div>
                        <div className={cn("mt-1 text-[10px]", mine ? "text-white/70" : "text-muted-foreground")}>{String(m.created_at).slice(11, 16)}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="flex items-center gap-2 border-t p-3">
                <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Write a message…" className="h-10 flex-1 rounded-md border bg-background px-3 text-sm outline-none focus:border-[#155EEF] focus:ring-2 focus:ring-[#155EEF]/20" />
                <button data-testid="send-message-btn" onClick={send} disabled={sending || !text.trim()}
                  className="flex h-10 w-10 items-center justify-center rounded-md bg-[#155EEF] text-white transition-colors hover:bg-[#0F4FD1] disabled:opacity-50">
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {composeOpen && <ComposeModal onClose={() => setComposeOpen(false)} onCreated={async (id) => { setComposeOpen(false); await loadConvos(); openConvo(id); }} />}
    </div>
  );
}

function ComposeModal({ onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/staff-messages/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const toggle = (email) => setSelected((s) => (s.includes(email) ? s.filter((x) => x !== email) : [...s, email]));
  const submit = async () => {
    if (selected.length === 0 || !body.trim()) return;
    setSaving(true);
    try { const { data } = await api.post("/staff-messages/conversations", { subject, participant_emails: selected, body }); toast.success("Message sent"); onCreated(data.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="compose-modal" className="careiq-scroll relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border bg-card p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between"><h3 className="text-lg font-bold">New Message</h3>
          <button data-testid="compose-close-btn" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={20} /></button></div>
        <div className="space-y-3">
          <div>
            <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Users size={13} /> Recipients</div>
            <div className="careiq-scroll max-h-40 space-y-1.5 overflow-y-auto rounded-lg border p-2">
              {users.map((u) => (
                <button key={u.email} data-testid={`recipient-${u.email}`} onClick={() => toggle(u.email)}
                  className={cn("flex w-full items-center gap-2 rounded-md border p-2 text-left text-sm transition-colors", selected.includes(u.email) ? "border-[#155EEF] bg-[#155EEF]/5" : "hover:bg-muted/40")}>
                  <span className={cn("flex h-5 w-5 items-center justify-center rounded border", selected.includes(u.email) ? "border-[#155EEF] bg-[#155EEF] text-white" : "")}>{selected.includes(u.email) && <Check size={13} />}</span>
                  <Avatar name={u.name} size={28} />
                  <span className="flex-1"><span className="font-medium">{u.name}</span> <span className="text-xs text-muted-foreground">· {ROLE_LABELS[u.role] || u.role}</span></span>
                </button>
              ))}
            </div>
          </div>
          <label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">Subject</span>
            <input data-testid="compose-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="h-10 w-full rounded-md border bg-background px-3 text-sm outline-none focus:border-[#155EEF]" /></label>
          <label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">Message</span>
            <textarea data-testid="compose-body" value={body} onChange={(e) => setBody(e.target.value)} rows={4} className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:border-[#155EEF]" /></label>
        </div>
        <button data-testid="compose-send-btn" disabled={saving || selected.length === 0 || !body.trim()} onClick={submit}
          className="mt-4 w-full rounded-md bg-[#155EEF] py-2.5 text-sm font-semibold text-white hover:bg-[#0F4FD1] disabled:opacity-50">{saving ? "Sending…" : "Send Message"}</button>
      </div>
    </div>
  );
}
